import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Theme,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { ChevronRight, Check } from '@material-ui/icons';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { useCampfireLazyQuery } from '../../../../../global/network/useCampfireLazyQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import {
  GetBillingPackageOptions,
  GetBillingPackageOptionsVariables,
  GetBillingPackageOptions_billingPackageOptions as BillingPackageOptionType,
} from '../../../../subscriptions/prices/__generated__/GetBillingPackageOptions';
import { GET_PRICES } from '../../../../subscriptions/prices/price-model.gql';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      minHeight: 'calc(100% - 20px)',
      backgroundColor: '#F9F9F9',
    },
    heading: {
      paddingBottom: '8px',
      borderBottom: '1px solid #efefef',
    },
    headingTitle: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      fontSize: 20,
    },
    headingDescription: {
      fontSize: '14px',
    },
    planCard: {
      height: '100%',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      transition: 'all 0.2s ease',
      backgroundColor: '#ffffff',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      '&:hover': {
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      },
    },
    planCardPopular: {
      borderColor: '#6E1C42',
      borderWidth: '2px',
      position: 'relative',
    },
    planHeader: {
      paddingBottom: '24px',
      borderBottom: '1px solid #f0f0f0',
    },
    planName: {
      fontSize: '18px',
      fontWeight: 600,
      marginBottom: '16px',
    },
    planPrice: {
      fontSize: '28px',
      fontWeight: 700,
      marginBottom: '4px',
      lineHeight: 1,
    },
    planPeriod: {
      fontSize: '16px',
      color: '#718096',
      fontWeight: 400,
    },
    planCapacity: {
      fontSize: '14px',
      marginTop: '18px',
    },
    featureList: {
      padding: '15px 20px',
    },
    featureItem: {
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: '2px',
    },
    featureIcon: {
      minWidth: '28px',
    },
    contentContainer: {
      background: 'white',
      paddingTop: '1rem',
      paddingBottom: '1.875rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '1rem',
      borderRadius: '4px',
    },
    sectionHeading: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      fontSize: 16,
      paddingBottom: 8,
    },
    gridItem: {
      paddingRight: '2rem',
    },
    featureText: {
      fontSize: '14px',
      fontWeight: 500,
      color: theme.color.grey.neutralBrand800,
    },
    selectButton: {
      width: '100%',
      padding: '14px 24px',
      textTransform: 'none',
      borderRadius: '0 0 6px 6px',
      fontWeight: 600,
      fontSize: '16px',
      marginTop: 'auto',
      color: 'white',
      border: 'none',
    },
    tableContainer: {
      marginTop: '2rem',
      marginBottom: '2rem',
    },
    tableHeader: {
      backgroundColor: '#f8f9fa',
      fontWeight: 600,
    },
    planColumn: {
      minWidth: '200px',
      fontWeight: 600,
    },
    tierRow: {
      '&:hover': {
        backgroundColor: '#f8f9fa',
      },
    },
    selectButtonSmall: {
      padding: '6px 12px',
      textTransform: 'none',
      borderRadius: '0 0 4px 4px',
      fontWeight: 500,
      fontSize: '14px',
      minWidth: '80px',
    },
  })
);

const planFeatures = {
  starter: [
    { text: 'Recruitment & Onboarding' },
    { text: 'Training & Compliance' },
    { text: 'Volunteer Database' },
    { text: 'Email Communications' },
  ],
  plus: [
    { text: 'Recruitment & Onboarding' },
    { text: 'Training & Compliance' },
    { text: 'Volunteer Database' },
    { text: 'Email & SMS' },
    { text: 'Rostering & Scheduling' },
    { text: 'Reporting' },
  ],
  pro: [
    { text: 'Recruitment & Onboarding' },
    { text: 'Training & Compliance' },
    { text: 'Volunteer Database' },
    { text: 'Email & SMS' },
    { text: 'Rostering & Scheduling' },
    { text: 'Complex Reporting & Insights' },
    { text: 'Incident Management' },
    { text: 'Integrations' },
  ],
};

interface PricingPlansProps {
  cardOnly?: boolean;
}

export const PricingPlans = ({ cardOnly = false }: PricingPlansProps) => {
  const classes = useStyles();
  const { isSm } = useCampfireTheme();
  const [getBillingPackageOptions, { loading }] = useCampfireLazyQuery<
    GetBillingPackageOptions,
    GetBillingPackageOptionsVariables
  >(GET_PRICES);
  const [planOptions, setPlanOptions] = useState<BillingPackageOptionType[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const apiUrl = useApiUrl();
  const campfireCheckout = useCampfireFetch<{ url: string }>({ defer: true });

  useEffect(() => {
    getBillingPackageOptions({ variables: { period: 'monthly', basePlansOnly: true } }).then((response) => {
      // Server now returns only base plans for the specified period
      const basePlans = (response.data?.billingPackageOptions || []).filter((option) =>
        ['starter', 'plus', 'pro'].includes(option.billingPackage.packageType)
      );

      console.log('Available base plan options:', basePlans);
      setPlanOptions(basePlans);
    });
  }, [getBillingPackageOptions]);

  const handleSelectPlan = (stripePriceId: string, billingPackageOptionId: string) => {
    console.log(
      'Starting checkout with stripePriceId:',
      stripePriceId,
      'billingPackageOptionId:',
      billingPackageOptionId
    );
    campfireCheckout
      .run({
        url: `${apiUrl}/tm/subscriptions/checkout`,
        method: 'post',
        data: {
          priceId: stripePriceId,
          billingPackageOptionId: billingPackageOptionId,
        },
      })
      .then((response) => {
        console.log('Checkout response:', response);
        const { url } = response.data.data;
        console.log('Redirect URL:', url);
        if (url) {
          window.location.replace(url);
        } else {
          console.error('No URL returned from checkout');
        }
      })
      .catch((error) => {
        console.error('Checkout error:', error);
      });
  };

  // Map API data to our static plan structure
  const getStaticPlanData = (planName: string) => {
    switch (planName?.toLowerCase()) {
      case 'starter':
        return {
          name: 'Starter',
          price: 19,
          features: planFeatures.starter,
          isPopular: false,
          backgroundColor: '#95DCDC',
          color: '#0B627F',
          description: 'Safeguard your organisation with automated onboarding, compliance and training',
        };
      case 'plus':
        return {
          name: 'Plus',
          price: 49,
          features: planFeatures.plus,
          isPopular: true,
          backgroundColor: '#6E1C42',
          color: '#fff',
          description: 'Get back to what’s important by automating your operations and communications',
        };
      case 'pro':
        return {
          name: 'Pro',
          price: 99,
          features: planFeatures.pro,
          isPopular: false,
          backgroundColor: '#0B627F',
          color: '#fff',
          description:
            'Grow your organisation with advanced functionality, actionable insights and innovative workflows',
        };
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box className={classes.container} display='flex' justifyContent='center' alignItems='center'>
        <CircularProgress size={48} />
      </Box>
    );
  }

  // If cardOnly mode, just return the grid of cards without container/headers
  if (cardOnly) {
    return (
      <Grid container spacing={4} justifyContent='center'>
        {['starter', 'plus', 'pro'].map((planType) => {
          const staticData = getStaticPlanData(planType);
          if (!staticData) return null;

          return (
            <Grid item xs={12} sm={6} md={4} key={planType}>
              <Card className={`${classes.planCard} ${staticData.isPopular ? classes.planCardPopular : ''}`}>
                <CardContent style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: 0 }}>
                  <Box
                    className={classes.planHeader}
                    style={{
                      backgroundColor: staticData.backgroundColor,
                      color: staticData.color,
                      padding: '15px 20px',
                    }}
                  >
                    <Typography className={classes.planName}>{staticData.name}</Typography>
                    <Box>
                      <Typography variant='body2' style={{ fontSize: '13px' }}>
                        from
                      </Typography>
                      <Typography className={classes.planPrice}>
                        <small style={{ fontSize: '11px' }}>AUD</small>${staticData.price}/month
                      </Typography>
                    </Box>
                    <Typography className={classes.planCapacity}>{staticData.description}</Typography>
                  </Box>

                  <Box
                    display={'flex'}
                    style={{ flexDirection: 'column', justifyContent: 'space-between', flexGrow: 2 }}
                  >
                    <List className={classes.featureList}>
                      <Typography
                        variant='caption'
                        style={{
                          fontWeight: 600,
                          fontSize: '12px',
                          color: '#666',
                          paddingBottom: '8px',
                          display: 'block',
                        }}
                      >
                        Features
                      </Typography>
                      {staticData.features.map((feature) => (
                        <ListItem key={feature.text} className={classes.featureItem}>
                          <ListItemIcon className={classes.featureIcon}>
                            <Check fontSize='small' />
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography className={classes.featureText}>{feature.text}</Typography>}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  <Box paddingX={'20px'} paddingBottom={'10px'}>
                    <Typography
                      variant='body2'
                      component='a'
                      href='https://volaby.org/pricing'
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{
                        fontSize: '13px',
                        color: '#6E1C42',
                        textDecoration: 'none',
                        fontWeight: 500,
                      }}
                    >
                      View full pricing details →
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  }

  // Full component with headers and descriptions
  return (
    <Box className={classes.container}>
      <Box className={classes.heading}>
        <Typography className={classes.headingTitle}>Billing</Typography>
        <Typography className={classes.headingDescription}>Choose your Volaby plan</Typography>
      </Box>

      <Box style={{ marginTop: '25px', marginBottom: '25px' }} className={classes.contentContainer}>
        <Box
          display='flex'
          flexDirection={isSm ? 'column' : 'row'}
          alignItems={isSm ? 'left' : 'center'}
          justifyContent='space-between'
          pr='1.25rem'
          pt={isSm ? '1rem' : 0}
        >
          <Box>
            <Typography style={{ fontWeight: 600, fontSize: '16px', display: 'flex', alignItems: 'center' }}>
              A Guide to Pricing
            </Typography>
            <Typography variant={'body2'} style={{ fontSize: '14px', color: '#808080', paddingBottom: '10px' }}>
              {'Understanding your Volaby subscription'}
            </Typography>

            <Typography variant={'body2'} style={{ fontSize: '15px', color: '#505050' }}>
              Volaby subscriptions grow with your organisation - you only pay for what you use. <br />
              Your subscription will be assigned a tier, corresponding to an amount of active volunteers, or users,
              within your account. Tiers also include a set amount of administrators and program managers.
              <br />
              <br />
              <a
                href='https://volaby.org/pricing'
                target='_blank'
                rel='noopener noreferrer'
                style={{ color: '#6E1C42', textDecoration: 'none', fontWeight: 600 }}
              >
                View full pricing details →
              </a>
              <br />
              <br />
              <strong>
                <small>
                  NFP discounts available. ACNC (or local equivalent) registration required and applicable after first
                  month&apos;s bill.
                  <br /> To arrange, get in contact with us after setting up billing.
                </small>
              </strong>
            </Typography>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={4} justifyContent='center'>
        {['starter', 'plus', 'pro'].map((planType) => {
          const staticData = getStaticPlanData(planType);
          if (!staticData) return null;

          // Find matching plan from API by package type
          const apiPlan = planOptions.find((option) => {
            return option.billingPackage.packageType === planType;
          });

          console.log(`Looking for ${planType} plan:`, apiPlan);

          const isProcessing = campfireCheckout.isLoading && selectedPlan === apiPlan?.billingPackageOptionId;

          return (
            <Grid item xs={12} sm={6} md={4} key={planType}>
              <Card className={`${classes.planCard} ${staticData.isPopular ? classes.planCardPopular : ''}`}>
                <CardContent style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: 0 }}>
                  <Box
                    className={classes.planHeader}
                    style={{
                      backgroundColor: staticData.backgroundColor,
                      color: staticData.color,
                      padding: '15px 20px',
                    }}
                  >
                    <Typography className={classes.planName}>{staticData.name}</Typography>
                    <Box>
                      <Typography variant='body2' style={{ fontSize: '13px' }}>
                        from
                      </Typography>
                      <Typography className={classes.planPrice}>
                        <small style={{ fontSize: '11px' }}>AUD</small>${staticData.price}/month
                      </Typography>
                    </Box>
                    <Typography className={classes.planCapacity}>{staticData.description}</Typography>
                  </Box>

                  <Box
                    display={'flex'}
                    style={{ flexDirection: 'column', justifyContent: 'space-between', flexGrow: 2 }}
                  >
                    <List className={classes.featureList}>
                      <Typography
                        variant='caption'
                        style={{
                          fontWeight: 600,
                          fontSize: '12px',
                          color: '#666',
                          paddingBottom: '8px',
                          display: 'block',
                        }}
                      >
                        Features
                      </Typography>
                      {staticData.features.map((feature) => (
                        <ListItem key={feature.text} className={classes.featureItem}>
                          <ListItemIcon className={classes.featureIcon}>
                            <Check fontSize='small' />
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography className={classes.featureText}>{feature.text}</Typography>}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  <Button
                    variant='contained'
                    className={classes.selectButton}
                    style={{ backgroundColor: staticData.backgroundColor, color: staticData.color }}
                    endIcon={isProcessing ? <CircularProgress size={16} color='inherit' /> : <ChevronRight />}
                    disabled={!apiPlan || campfireCheckout.isLoading}
                    onClick={() => {
                      if (apiPlan) {
                        setSelectedPlan(apiPlan.billingPackageOptionId);
                        // Parse the Stripe price ID from the stripePrice JSON
                        try {
                          const stripePrice = JSON.parse(apiPlan.stripePrice || '{}');
                          const stripePriceId = stripePrice.id;
                          if (stripePriceId) {
                            handleSelectPlan(stripePriceId, apiPlan.billingPackageOptionId);
                          } else {
                            console.error('No Stripe price ID found in:', stripePrice);
                          }
                        } catch (error) {
                          console.error('Error parsing stripePrice:', apiPlan.stripePrice, error);
                        }
                      }
                    }}
                  >
                    {isProcessing ? 'Processing...' : 'Select'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
